import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "@/components/ui/use-toast";
import { api, apiErrorMessage } from "@/lib/api";
import { useApiQuery } from "@/lib/use-api-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";

/**
 * `/projects/:slug/keys`: the project's Vault items. The list carries no
 * secrets; Reveal fetches one item's, and Hub records who did.
 */

export const Route = createFileRoute("/_app/projects/$slug/keys")({
  component: KeysPage,
});

type Key = {
  id: string;
  name: string;
  prod: boolean;
  username: string | null;
  uris: string[];
  hasPassword: boolean;
  hasTotp: boolean;
  hasNotes: boolean;
  fields: string[];
};
type Revealed = {
  password: string | null;
  totp: string | null;
  notes: string | null;
  fields: Array<{ name: string; value: string }>;
};

/** Revealed secrets don't stay on screen. */
const HIDE_AFTER_MS = 60_000;

async function copy(label: string, value: string) {
  try {
    await navigator.clipboard.writeText(value);
    toast({ title: `${label} copied` });
  } catch {
    toast({ title: `Couldn't copy the ${label.toLowerCase()}`, variant: "destructive" });
  }
}

function KeysPage() {
  const { slug } = Route.useParams();
  const base = `/api/projects/${encodeURIComponent(slug)}/keys`;
  const { data, error, loading } = useApiQuery(
    () => api.get<{ items: Key[] }>(base, { timeoutMs: 60_000 }),
    [slug],
  );
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();
  const items = (data?.items ?? []).filter(
    (k) =>
      !q ||
      k.name.toLowerCase().includes(q) ||
      (k.username ?? "").toLowerCase().includes(q) ||
      k.uris.some((u) => u.toLowerCase().includes(q)),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">
          Items from the project's Vault collections. Every reveal is recorded.
        </p>
        <input
          type="search"
          className="h-9 w-full max-w-xs rounded-md border border-input bg-background px-2 text-sm"
          placeholder="Search keys"
          aria-label="Search keys"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      {loading && <p className="text-sm text-muted-foreground">Loading…</p>}
      {error && <p className="text-sm text-destructive">{apiErrorMessage(error)}</p>}
      {data && items.length === 0 && (
        <p className="text-sm text-muted-foreground">
          {q ? "Nothing matches." : "No items in this project's Vault collections."}
        </p>
      )}
      {items.length > 0 && (
        <Card>
          <CardContent className="divide-y pt-2">
            {items.map((k) => (
              <KeyRow key={k.id} item={k} base={base} />
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KeyRow({ item, base }: { item: Key; base: string }) {
  const [revealed, setRevealed] = useState<Revealed | null>(null);
  const [busy, setBusy] = useState(false);
  const secret = item.hasPassword || item.hasTotp || item.hasNotes || item.fields.length > 0;

  useEffect(() => {
    if (!revealed) return;
    const t = setTimeout(() => setRevealed(null), HIDE_AFTER_MS);
    return () => clearTimeout(t);
  }, [revealed]);

  async function reveal() {
    setBusy(true);
    try {
      setRevealed(
        await api.post<Revealed>(`${base}/${encodeURIComponent(item.id)}/reveal`, undefined, {
          timeoutMs: 60_000,
        }),
      );
    } catch (err) {
      toast({
        title: "Couldn't reveal it",
        description: apiErrorMessage(err),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2 py-3" data-testid="key-row">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm">
          {item.prod && <Badge variant="destructive">prod</Badge>}
          <span className="font-medium">{item.name}</span>
          {item.username && (
            <button
              type="button"
              className="font-mono text-muted-foreground hover:text-foreground"
              title="Copy the username"
              onClick={() => void copy("Username", item.username ?? "")}
            >
              {item.username}
            </button>
          )}
          {item.uris.map((u) => (
            <a
              key={u}
              href={/^https?:\/\//.test(u) ? u : `https://${u}`}
              target="_blank"
              rel="noreferrer"
              className="truncate text-primary hover:underline"
            >
              {u}
            </a>
          ))}
        </div>
        {secret && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={busy}
            aria-label={`${revealed ? "Hide" : "Reveal"} ${item.name}`}
            onClick={() => (revealed ? setRevealed(null) : void reveal())}
          >
            {revealed ? "Hide" : busy ? "Revealing…" : "Reveal"}
          </Button>
        )}
      </div>
      {revealed && (
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
          {revealed.password && <Secret label="Password" value={revealed.password} />}
          {revealed.totp && <Secret label="TOTP" value={revealed.totp} />}
          {revealed.fields.map((f) => (
            <Secret key={f.name} label={f.name} value={f.value} />
          ))}
          {revealed.notes && (
            <>
              <dt className="text-muted-foreground">Notes</dt>
              <dd className="whitespace-pre-wrap break-words">{revealed.notes}</dd>
            </>
          )}
        </dl>
      )}
    </div>
  );
}

function Secret({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 items-center gap-2">
        <code className="break-all">{value}</code>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label={`Copy ${label}`}
          onClick={() => void copy(label, value)}
        >
          Copy
        </Button>
      </dd>
    </>
  );
}
